'use client';

import React, { useState, useMemo } from 'react';
import Link from 'next/link';
import { LeadCaptureForm } from '@/components/calculator/LeadCaptureForm';

// Each option carries a risk weight (higher = more risk) and a gap flag that
// surfaces a specific recommendation when chosen.
interface Option {
  text: string;
  risk: number; // 0 (good) .. 3 (worst)
  gap?: string; // recommendation triggered when this option is selected
}

interface Question {
  id: string;
  text: string;
  helpText?: string;
  options: Option[];
}

const QUESTIONS: Question[] = [
  {
    id: 'access_method',
    text: 'How do staff connect to internal apps and servers remotely?',
    helpText: 'Flat VPNs let threats move laterally; zero-trust grants per-app access.',
    options: [
      { text: 'Zero-trust access — per-app, identity-based', risk: 0 },
      { text: 'Traditional VPN giving broad network access', risk: 2, gap: 'A flat VPN puts users on the network and enables lateral movement. Move to per-app zero-trust access that never exposes the whole network.' },
      { text: 'RDP / ports exposed directly to the internet', risk: 3, gap: 'Internet-exposed RDP/ports are a top ransomware entry point. Put every remote service behind zero-trust access immediately.' },
      { text: 'Mix / not sure', risk: 2, gap: 'Inconsistent remote access leaves exposed paths. Standardize on identity-based zero-trust access.' },
    ],
  },
  {
    id: 'mfa_remote',
    text: 'Is MFA enforced on all remote access?',
    helpText: 'MFA blocks the vast majority of credential attacks.',
    options: [
      { text: 'Yes — MFA on every remote connection', risk: 0 },
      { text: 'On VPN but not every service', risk: 1, gap: 'MFA gaps on individual services undermine the model. Enforce MFA on every remote entry point.' },
      { text: 'No MFA on remote access / not sure', risk: 3, gap: 'Remote access without MFA is one stolen password away from compromise. Enforcing MFA is the top priority.' },
    ],
  },
  {
    id: 'third_party',
    text: 'How do contractors and vendors get access?',
    helpText: 'Third-party access is a common breach path.',
    options: [
      { text: 'Scoped, time-limited, per-app access', risk: 0 },
      { text: 'Same VPN/account access as employees', risk: 2, gap: 'Broad employee-level access for third parties is a major risk. Scope contractor access to specific apps for a limited time.' },
      { text: 'Shared logins / unmanaged / not sure', risk: 3, gap: 'Shared or unmanaged third-party access is a frequent breach path. Provision per-identity, least-privilege access that’s easy to revoke.' },
    ],
  },
  {
    id: 'device_posture',
    text: 'Are devices checked for security posture before they connect?',
    helpText: 'Compromised or unpatched devices shouldn’t reach internal apps.',
    options: [
      { text: 'Yes — posture (patching, encryption, EDR) is verified', risk: 0 },
      { text: 'Only company devices are checked', risk: 1, gap: 'Unmanaged and personal devices connect unchecked. Verify posture for every connection, not just corporate laptops.' },
      { text: 'No device checks / not sure', risk: 2, gap: 'Without posture checks, compromised devices reach internal apps. Add device-posture gating.' },
    ],
  },
  {
    id: 'segmentation',
    text: 'If one account is compromised, how far can an attacker move?',
    helpText: 'Segmentation contains the blast radius.',
    options: [
      { text: 'Access is per-app; lateral movement is blocked', risk: 0 },
      { text: 'Some segmentation, but VPN users see a broad network', risk: 2, gap: 'Broad network visibility enables lateral movement. Segment access so a compromised user can’t reach everything.' },
      { text: 'Flat network — one foothold reaches everything / not sure', risk: 3, gap: 'A flat network turns one compromised login into full access. Zero-trust segmentation contains the blast radius.' },
    ],
  },
  {
    id: 'offboarding',
    text: 'When access should be removed, how fast and complete is it?',
    helpText: 'Lingering access for former staff/vendors is a real exposure.',
    options: [
      { text: 'Instant, centralized revocation across all apps', risk: 0 },
      { text: 'Within a day or two', risk: 1, gap: 'Multi-day revocation leaves active access for departed users/contractors. Centralize so access is revoked instantly.' },
      { text: 'Manual / inconsistent / not sure', risk: 3, gap: 'Lingering remote access for former staff and vendors is a real exposure. Centralized zero-trust access revokes everything in one step.' },
    ],
  },
];

type Status = 'intro' | 'assessment' | 'results';

interface Answer {
  questionId: string;
  optionIndex: number;
}

function levelFor(percent: number): {
  level: 'High' | 'Medium' | 'Low';
  color: string;
  bg: string;
  blurb: string;
} {
  if (percent >= 55) {
    return {
      level: 'High',
      color: 'text-red-700 dark:text-red-300',
      bg: 'bg-red-50 dark:bg-red-950/40 border-red-200 dark:border-red-800',
      blurb:
        'Your remote access has serious gaps—the kind that turn one compromised login into a full breach. Replacing flat VPN/RDP access with zero-trust should be an immediate priority.',
    };
  }
  if (percent >= 25) {
    return {
      level: 'Medium',
      color: 'text-amber-700 dark:text-amber-300',
      bg: 'bg-amber-50 dark:bg-amber-950/40 border-amber-200 dark:border-amber-800',
      blurb:
        'You have some controls in place but meaningful gaps remain. Closing them—especially MFA coverage, third-party access, and segmentation—will materially lower your breach risk.',
    };
  }
  return {
    level: 'Low',
    color: 'text-green-700 dark:text-green-300',
    bg: 'bg-green-50 dark:bg-green-950/40 border-green-200 dark:border-green-800',
    blurb:
      'Your remote access posture is strong. A managed zero-trust service keeps it that way—enforcing per-app access, device posture, and instant revocation as you grow.',
  };
}

export default function RiskCheckClient() {
  const [status, setStatus] = useState<Status>('intro');
  const [currentIndex, setCurrentIndex] = useState(0);
  const [answers, setAnswers] = useState<Answer[]>([]);
  const [userCount, setUserCount] = useState<number>(25);

  const handleAnswer = (optionIndex: number) => {
    const question = QUESTIONS[currentIndex];
    const newAnswer: Answer = { questionId: question.id, optionIndex };
    const newAnswers = [...answers.filter((a) => a.questionId !== question.id), newAnswer];
    setAnswers(newAnswers);

    if (currentIndex < QUESTIONS.length - 1) {
      setCurrentIndex(currentIndex + 1);
    } else {
      setStatus('results');
    }
  };

  const handleBack = () => {
    if (currentIndex > 0) setCurrentIndex(currentIndex - 1);
  };

  const handleRestart = () => {
    setAnswers([]);
    setCurrentIndex(0);
    setStatus('intro');
  };

  const result = useMemo(() => {
    if (answers.length !== QUESTIONS.length) return null;
    let total = 0;
    const maxPerQuestion = 3;
    const gaps: string[] = [];
    QUESTIONS.forEach((q) => {
      const a = answers.find((ans) => ans.questionId === q.id);
      if (!a) return;
      const option = q.options[a.optionIndex];
      total += option.risk;
      if (option.gap) gaps.push(option.gap);
    });
    const max = QUESTIONS.length * maxPerQuestion;
    const percent = Math.round((total / max) * 100);
    return { percent, gaps, ...levelFor(percent) };
  }, [answers]);

  const resultsSummary = useMemo(() => {
    if (!result) return '';
    const answerLines = QUESTIONS.map((q) => {
      const a = answers.find((ans) => ans.questionId === q.id);
      const chosen = a ? q.options[a.optionIndex].text : 'N/A';
      return `  • ${q.text} → ${chosen}`;
    }).join('\n');

    const gapLines = result.gaps.length
      ? result.gaps.map((g, i) => `${i + 1}. ${g}`).join('\n')
      : 'No major gaps identified—maintain current controls.';

    return `REMOTE ACCESS RISK CHECK RESULTS
━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
Remote Access Risk: ${result.level} (${result.percent}% risk score)
Approx. remote users / contractors: ${userCount}

${result.blurb}

ANSWERS:
${answerLines}

TOP GAPS TO CLOSE:
${gapLines}`;
  }, [result, answers, userCount]);

  // Intro
  if (status === 'intro') {
    return (
      <div className="max-w-2xl mx-auto">
        <div className="bg-white dark:bg-gray-800 rounded-2xl border-2 border-gray-200 dark:border-gray-700 p-8 shadow-lg">
          <div className="text-center mb-8">
            <div className="text-5xl mb-4">🖥️</div>
            <h2 className="text-2xl font-bold text-gray-900 dark:text-gray-100 mb-3">How exposed is your remote access?</h2>
            <p className="text-gray-600 dark:text-gray-400 max-w-lg mx-auto">
              Six questions on remote access method, MFA, third-party access, device posture, segmentation, and offboarding. Get an instant Remote Access Risk score and your top gaps.
            </p>
          </div>

          <div className="bg-[#2813e8]/5 border border-[#2813e8]/20 rounded-lg p-4 mb-6">
            <h3 className="font-semibold text-[#2813e8] mb-2">What you&apos;ll learn:</h3>
            <ul className="text-gray-700 dark:text-gray-300 text-sm space-y-1">
              <li>• Whether your remote access risk is High, Medium, or Low</li>
              <li>• The specific gaps leaving you exposed</li>
              <li>• What to fix first to close the biggest holes</li>
            </ul>
          </div>

          <button
            onClick={() => setStatus('assessment')}
            className="w-full bg-gradient-to-r from-[#2813e8] to-[#2cc3d1] hover:opacity-90 text-white font-semibold py-3 px-6 rounded-lg transition-opacity"
          >
            Start the Risk Check
          </button>
        </div>
      </div>
    );
  }

  // Assessment
  if (status === 'assessment') {
    const question = QUESTIONS[currentIndex];
    const progress = ((currentIndex + 1) / QUESTIONS.length) * 100;
    const currentAnswer = answers.find((a) => a.questionId === question.id);

    return (
      <div className="max-w-2xl mx-auto">
        <div className="bg-white dark:bg-gray-800 rounded-2xl border-2 border-gray-200 dark:border-gray-700 p-8 shadow-lg">
          <div className="mb-6">
            <div className="flex justify-between text-sm text-gray-600 dark:text-gray-400 mb-2">
              <span>Remote Access Risk Check</span>
              <span>Question {currentIndex + 1} of {QUESTIONS.length}</span>
            </div>
            <div className="h-2 bg-gray-200 dark:bg-gray-700 rounded-full overflow-hidden">
              <div className="h-full bg-gradient-to-r from-[#2813e8] to-[#2cc3d1] transition-all duration-300" style={{ width: `${progress}%` }} />
            </div>
          </div>

          <div className="mb-6">
            <h2 className="text-xl font-semibold text-gray-900 dark:text-gray-100 mb-2">{question.text}</h2>
            {question.helpText && <p className="text-gray-600 dark:text-gray-400 text-sm">{question.helpText}</p>}
          </div>

          <div className="space-y-3 mb-6">
            {question.options.map((option, idx) => (
              <button
                key={idx}
                onClick={() => handleAnswer(idx)}
                className={`w-full text-left p-4 rounded-lg border-2 transition-all ${
                  currentAnswer?.optionIndex === idx
                    ? 'border-[#2813e8] bg-[#2813e8]/5'
                    : 'border-gray-200 dark:border-gray-700 hover:border-[#2813e8]/40 hover:bg-gray-50 dark:hover:bg-gray-700'
                }`}
              >
                <span className="text-gray-900 dark:text-gray-100">{option.text}</span>
              </button>
            ))}
          </div>

          <div className="flex justify-between">
            <button
              onClick={handleBack}
              disabled={currentIndex === 0}
              className="px-4 py-2 text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-100 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              ← Back
            </button>
            <button
              onClick={handleRestart}
              className="px-4 py-2 text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300"
            >
              Start Over
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Results
  if (status === 'results' && result) {
    return (
      <div className="max-w-3xl mx-auto space-y-6">
        {/* Score */}
        <div className={`rounded-2xl border-2 p-8 ${result.bg}`}>
          <div className="text-center mb-6">
            <div className="text-5xl mb-4">{result.level === 'High' ? '🚨' : result.level === 'Medium' ? '⚠️' : '✅'}</div>
            <h2 className={`text-3xl font-bold mb-2 ${result.color}`}>Remote Access Risk: {result.level}</h2>
            <p className="text-gray-700 dark:text-gray-300 max-w-xl mx-auto">{result.blurb}</p>
          </div>
          <div className="bg-white/60 dark:bg-gray-800/60 rounded-lg p-4 text-center">
            <div className="text-sm text-gray-600 dark:text-gray-400 mb-1">Risk score</div>
            <div className={`text-3xl font-bold ${result.color}`}>{result.percent}%</div>
          </div>
        </div>

        {/* Gaps */}
        <div className="bg-white dark:bg-gray-800 rounded-2xl border-2 border-gray-200 dark:border-gray-700 p-6 shadow-sm">
          <h3 className="text-lg font-bold text-gray-900 dark:text-gray-100 mb-4">
            {result.gaps.length ? 'Top Gaps to Close' : 'No Major Gaps Identified'}
          </h3>
          {result.gaps.length ? (
            <ul className="space-y-3">
              {result.gaps.map((gap, i) => (
                <li key={i} className="flex items-start gap-3">
                  <span className="flex-shrink-0 mt-0.5 flex h-6 w-6 items-center justify-center rounded-full bg-[#2813e8]/10 text-xs font-bold text-[#2813e8]">{i + 1}</span>
                  <span className="text-gray-700 dark:text-gray-300 leading-relaxed">{gap}</span>
                </li>
              ))}
            </ul>
          ) : (
            <p className="text-gray-600 dark:text-gray-400">Your controls look solid. A managed zero-trust service keeps per-app access, device posture, and revocation tight as you scale.</p>
          )}
        </div>

        {/* User count */}
        <div className="bg-white dark:bg-gray-800 rounded-2xl border-2 border-gray-200 dark:border-gray-700 p-6 shadow-sm">
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
            How many remote users and contractors need access? <span className="text-[#2813e8] font-bold">{userCount}</span>
          </label>
          <input
            type="range"
            min="1"
            max="500"
            step="1"
            value={userCount}
            onChange={(e) => setUserCount(Number(e.target.value))}
            aria-label="Number of remote users and contractors"
            className="w-full h-2 bg-gray-200 dark:bg-gray-700 rounded-lg appearance-none cursor-pointer accent-[#2813e8]"
          />
          <div className="flex justify-between text-xs text-gray-500 dark:text-gray-400 mt-1">
            <span>1</span>
            <span>250</span>
            <span>500</span>
          </div>
        </div>

        {/* Offer CTA */}
        <div className="rounded-2xl border-2 border-[#2813e8]/30 bg-[#2813e8]/5 p-6 text-center">
          <h3 className="text-lg font-bold text-gray-900 dark:text-gray-100 mb-2">Want these gaps closed for you?</h3>
          <p className="text-gray-600 dark:text-gray-400 mb-4">
            Inventive HQ replaces flat VPNs with managed zero-trust remote access—per-app, identity-based, and continuously verified.
          </p>
          <Link
            href="/services/zero-trust-remote-access"
            className="inline-flex items-center justify-center gap-2 rounded-lg bg-gradient-to-r from-[#2813e8] to-[#2cc3d1] px-8 py-3 font-semibold text-white shadow-lg transition-all hover:scale-105"
          >
            See Zero Trust Remote Access
          </Link>
        </div>

        {/* Actions */}
        <div className="flex flex-col sm:flex-row gap-3">
          <button
            onClick={handleRestart}
            className="flex-1 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-700 text-gray-700 dark:text-gray-300 font-medium py-3 px-6 rounded-lg transition-colors"
          >
            🔄 Retake Check
          </button>
        </div>

        {/* Lead capture */}
        <div className="mt-8">
          <LeadCaptureForm
            source="zero-trust"
            title="Get Your Remote Access Risk Reviewed by an Expert"
            description="Our team will review your results and send a prioritized plan to move from VPN to zero-trust remote access."
            deliverable="a prioritized plan to move from VPN to zero-trust remote access"
            resultsSummary={resultsSummary}
          />
        </div>
      </div>
    );
  }

  return null;
}
